import { format } from 'date-fns'
import type { ReleaseNote } from '@/lib/api/releaseNotesService'
import type { Notification } from '@/lib/api/notificationService'

export const VERSION_TYPE_LABELS: Record<string, string> = {
  PROCESS_CHANGE: 'Process Change',
  PROCESS_IMPROVEMENT: 'Process Improvement',
  NEW_FEATURE: 'New Feature',
}

export const VERSION_TYPE_COLOR_CLASS: Record<string, string> = {
  PROCESS_IMPROVEMENT: 'text-blue-600',
  PROCESS_CHANGE: 'text-amber-600',
  NEW_FEATURE: 'text-green-600',
}

/** Angular `.unread-content` background for drawer list rows. */
export const DRAWER_UNREAD_LIST_BG = 'bg-[#F0F8FF]'
export const DRAWER_READ_LIST_BG = 'bg-white'

export function getDrawerListItemBackgroundClass(isUnread: boolean): string {
  return isUnread ? DRAWER_UNREAD_LIST_BG : DRAWER_READ_LIST_BG
}

/** Avatar initials from first + last name (e.g. "Pranav Saykar" → "PS"). */
export function getUserInitials(
  firstName?: string | null,
  lastName?: string | null,
): string {
  const first = String(firstName ?? '').trim()
  const last = String(lastName ?? '').trim()
  const a = first.charAt(0)
  const b = last.charAt(0)
  if (a && b) return `${a}${b}`.toUpperCase()
  if (first.length >= 2) return first.slice(0, 2).toUpperCase()
  if (a) return a.toUpperCase()
  return 'U'
}

export const NOTES_FOR_LABELS: Record<string, string> = {
  MEETING: 'Meeting',
  TODO: 'Todo',
  TRAINING: 'Training',
}

/** Strip HTML tags for list preview text (matches Angular substring on raw HTML). */
export function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return `${text.substring(0, maxLength)}...`
}

export function getReleaseNoteListPreview(note: ReleaseNote): string {
  if (!note.version_description) return ''
  return truncateText(stripHtml(note.version_description), 150)
}

export function isReleaseNoteUnread(note: ReleaseNote): boolean {
  const read = note.is_read
  if (read === '1' || read === 1) return false
  return true
}

export function isAnnouncementUnread(
  announcement: { is_read?: number | string | null },
): boolean {
  const read = announcement.is_read
  if (read === 1 || read === '1') return false
  return true
}

/** Parse notification `created_at` (seconds or ms) for display. */
export function notificationCreatedAtDate(createdAt: number): Date {
  const ms = createdAt > 1_000_000_000_000 ? createdAt : createdAt * 1000
  return new Date(ms)
}

/** Unread when `is_read` is not exactly `'1'` (matches Angular list + checkIsRead). */
export function isNotificationUnread(notification: Notification): boolean {
  return notification.is_read !== '1'
}

export function hasUnreadNotifications(notifications: Notification[]): boolean {
  return notifications.some((n) => !n.is_read)
}

/** Format API date strings for TRAINING/MEETING/TODO list rows (matches Angular date pipe). */
export function formatNotificationScheduleDate(
  value: string | null | undefined,
  part: 'date' | 'time',
): string {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  if (part === 'date') {
    return format(parsed, 'MM/dd/yyyy')
  }
  return format(parsed, 'hh:mm a')
}

export function buildNotificationPreviewText(notification: Notification): string {
  const name = `${notification.first_name} ${notification.last_name}`.trim()
  const notesFor = notification.notes_for ?? ''
  if (['TRAINING', 'TODO', 'MEETING'].includes(notesFor)) {
    const label = NOTES_FOR_LABELS[notesFor] ?? notesFor
    return `${name} assigned you to a ${label}`
  }
  return `${name} mentioned you in a note`
}
